(function () {
  'use strict';

  const KEYS = {
    local: 'hoya:welcome:browser:v1',
    session: 'hoya:welcome:session:v1',
    pending: 'hoya:welcome:pending:v1'
  };
  const SUPPRESSION_MS = 30 * 86400000;
  const PENDING_MS = 20 * 60000;

  function normalizedPath(path) {
    const clean = String(path || '/').split(/[?#]/)[0].replace(/\/+$/, '');
    return clean || '/';
  }

  function excludedPage(path, pageType) {
    return /(^|\/)(cart|checkout|checkouts|account|challenge|captcha|password|policies)(\/|$)/i.test(normalizedPath(path)) ||
      /\/pages\/(privacy(?:-policy)?|terms(?:-of-service)?)(\/|$)/i.test(normalizedPath(path)) ||
      /^(cart|policy|captcha|password|gift_card|customers\/)/.test(pageType || '');
  }

  function challengePage(path, pageType) {
    return /(^|\/)(challenge|captcha)(\/|$)/i.test(normalizedPath(path)) || pageType === 'captcha';
  }

  function previewAllowed({ requested = false, themeRole = '', designMode = false } = {}) {
    return designMode === true || requested && ['unpublished', 'development', 'demo'].includes(String(themeRole).toLowerCase());
  }

  function browserRecord(value) {
    return value && value.v === 1 && typeof value.subscribed === 'boolean' &&
      Number.isFinite(value.suppressUntil) && value.suppressUntil >= 0;
  }

  function sessionRecord(value) {
    return value && value.v === 1 && typeof value.shown === 'boolean' &&
      Number.isFinite(value.engagedMs) && value.engagedMs >= 0 && value.engagedMs <= 30000 &&
      Array.isArray(value.paths) && value.paths.length <= 50 &&
      new Set(value.paths).size === value.paths.length &&
      value.paths.every(path => typeof path === 'string' && path.startsWith('/') && normalizedPath(path) === path);
  }

  class WelcomePolicy {
    constructor({ localStorage, sessionStorage, preview = false, now = Date.now }) {
      this.stores = { local: localStorage, session: sessionStorage };
      this.now = now;
      this.preview = preview;
      this.ready = true;
      this.browser = { v: 1, subscribed: false, suppressUntil: 0 };
      this.session = { v: 1, shown: false, engagedMs: 0, paths: [] };
      if (preview) return;
      try {
        this.browser = this.read('local', KEYS.local, this.browser, browserRecord);
        this.session = this.read('session', KEYS.session, this.session, sessionRecord);
        this.legacySubscribed = this.store('local').getItem('theme:popup-filled') === 'true';
        this.write('local', KEYS.local, this.browser);
        this.write('session', KEYS.session, this.session);
      } catch (_) {
        this.ready = false;
      }
    }

    store(kind) {
      const source = this.stores[kind];
      return typeof source === 'function' ? source() : source;
    }

    read(kind, key, fallback, validate) {
      const raw = this.store(kind).getItem(key);
      if (raw === null) return fallback;
      const value = JSON.parse(raw);
      if (!validate(value)) throw new Error('Invalid welcome popup state');
      return value;
    }

    write(kind, key, value) {
      try {
        this.store(kind).setItem(key, JSON.stringify(value));
        return true;
      } catch (_) {
        this.ready = false;
        return false;
      }
    }

    visit(path, pageType) {
      if (this.preview || !this.ready || excludedPage(path, pageType)) return;
      const normalized = normalizedPath(path);
      if (!this.session.paths.includes(normalized) && this.session.paths.length < 50) {
        this.session.paths.push(normalized);
        this.write('session', KEYS.session, this.session);
      }
    }

    advance(milliseconds, context) {
      if (this.preview || !this.ready || this.session.shown || !this.safeContext(context) ||
        !Number.isFinite(milliseconds) || milliseconds <= 0) return;
      this.session.engagedMs = Math.min(30000, this.session.engagedMs + milliseconds);
      this.write('session', KEYS.session, this.session);
    }

    safeContext(context) {
      return context.visible && context.engaged && !context.typing && !context.competing &&
        !context.knownCustomer && !excludedPage(context.path, context.pageType);
    }

    eligible(context) {
      if (this.preview) return true;
      if (!this.ready || !this.safeContext(context) || this.session.shown || this.legacySubscribed ||
        this.session.paths.length < 2 || this.session.engagedMs < 30000) return false;
      try {
        this.browser = this.read('local', KEYS.local, this.browser, browserRecord);
      } catch (_) {
        this.ready = false;
        return false;
      }
      return !this.browser.subscribed && this.browser.suppressUntil <= this.now();
    }

    claim(context) {
      if (!this.eligible(context)) return false;
      if (this.preview) return true;
      this.browser.suppressUntil = this.now() + SUPPRESSION_MS;
      this.session.shown = true;
      return this.write('local', KEYS.local, this.browser) && this.write('session', KEYS.session, this.session);
    }

    dismiss() {
      if (this.preview) return;
      try {
        const latest = this.read('local', KEYS.local, this.browser, browserRecord);
        this.browser.subscribed = this.browser.subscribed || latest.subscribed;
        this.browser.suppressUntil = Math.max(this.browser.suppressUntil, latest.suppressUntil, this.now() + SUPPRESSION_MS);
      } catch (_) {
        this.ready = false;
        return;
      }
      this.session.shown = true;
      this.write('local', KEYS.local, this.browser);
      this.write('session', KEYS.session, this.session);
    }

    beginSubmission({ formId, token, path, source = 'popup' }) {
      if (!formId || typeof token !== 'string' || token.length < 16) return false;
      return this.write('session', KEYS.pending, {
        v: 1, formId, token, path: normalizedPath(path), submittedAt: this.now(), preview: this.preview,
        ...(source === 'newsletter' ? { source } : {})
      });
    }

    cancelSubmission() {
      try {
        this.store('session').removeItem(KEYS.pending);
      } catch (_) {
        this.ready = false;
      }
    }

    result({ formId, token, hash, postedSuccessfully, hasErrors, excluded = false, source = 'popup' }) {
      if (excluded) return null;
      let pending;
      try {
        pending = JSON.parse(this.store('session').getItem(KEYS.pending));
      } catch (_) {
        return null;
      }
      if (!pending || pending.v !== 1 || pending.formId !== formId || (pending.source || 'popup') !== source ||
        !Number.isFinite(pending.submittedAt) || pending.submittedAt > this.now() ||
        this.now() - pending.submittedAt > PENDING_MS) return null;
      const matches = token && token === pending.token;
      if (hasErrors && (matches || hash === '#' + formId)) {
        this.cancelSubmission();
        return 'error';
      }
      if (!matches || postedSuccessfully !== true || hasErrors) return null;
      this.cancelSubmission();
      if (!this.preview && !pending.preview) {
        this.browser.subscribed = true;
        this.dismiss();
      }
      return 'success';
    }
  }

  class WelcomePopupController {
    constructor(root, win) {
      this.root = root;
      this.win = win;
      this.doc = win.document;
      this.form = root.querySelector('form');
      this.lastTick = win.Date.now();
      this.engaged = false;
      this.presented = false;
      this.abort = new win.AbortController();
      const url = new URL(win.location.href);
      const designMode = root.dataset.designMode === 'true' || win.Shopify?.designMode === true;
      const role = win.Shopify?.theme?.role || root.dataset.themeRole;
      this.preview = previewAllowed({ requested: url.searchParams.get('hoya_welcome_preview') === '1', themeRole: role, designMode });
      this.policy = new WelcomePolicy({
        localStorage: () => win.localStorage,
        sessionStorage: () => win.sessionStorage,
        preview: this.preview,
        now: () => win.Date.now()
      });
      const result = root.querySelector('[data-welcome-result]');
      const outcome = this.policy.result({
        formId: this.form.id,
        token: url.searchParams.get('hoya_welcome_form') === this.form.id ? url.searchParams.get('hoya_welcome_token') : '',
        hash: url.hash,
        postedSuccessfully: result?.dataset.postedSuccessfully === 'true',
        hasErrors: result?.dataset.hasErrors === 'true',
        excluded: excludedPage(url.pathname, root.dataset.pageType)
      });
      const newsletterFormId = url.searchParams.get('hoya_newsletter_form');
      if (newsletterFormId && this.policy.result({
        formId: newsletterFormId,
        token: url.searchParams.get('hoya_newsletter_token'),
        hash: url.hash,
        postedSuccessfully: result?.dataset.postedSuccessfully === 'true',
        hasErrors: result?.dataset.hasErrors === 'true',
        excluded: challengePage(url.pathname, root.dataset.pageType),
        source: 'newsletter'
      })) {
        url.searchParams.delete('hoya_newsletter_form');
        url.searchParams.delete('hoya_newsletter_token');
        url.searchParams.delete('customer_posted');
        if (url.hash === '#' + newsletterFormId) url.hash = '';
        win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
      }
      this.automaticDisabled = result?.dataset.postedSuccessfully === 'true' || result?.dataset.hasErrors === 'true';
      this.policy.visit(url.pathname, root.dataset.pageType);
      const signal = this.abort.signal;
      const engage = () => {
        this.update();
        this.engaged = true;
      };
      for (const event of ['pointerdown', 'keydown', 'scroll', 'touchstart']) {
        this.doc.addEventListener(event, engage, { passive: true, signal });
      }
      this.doc.addEventListener('visibilitychange', () => this.update(), { signal });
      this.doc.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !root.hidden) {
          event.preventDefault();
          this.close();
        }
      }, { signal });
      root.querySelector('[data-welcome-close]').addEventListener('click', () => this.close(), { signal });
      win.addEventListener('submit', event => {
        if (event.target === this.form) {
          this.submit(event);
        } else {
          this.policy.cancelSubmission();
          if (!event.defaultPrevented) this.trackNewsletter(event.target);
        }
      }, { capture: true, signal });
      win.addEventListener('pagehide', () => {
        this.update();
        if (!root.hidden) this.close(false);
      }, { signal });
      win.addEventListener('pageshow', () => { this.lastTick = win.Date.now(); }, { signal });
      this.interval = win.setInterval(() => this.update(), 1000);
      if (outcome) {
        const success = root.querySelector('[data-welcome-success]');
        const errors = root.querySelector('[data-welcome-errors]');
        root.querySelector('[data-welcome-signup]').hidden = outcome === 'success';
        if (success) success.hidden = outcome !== 'success';
        if (errors) errors.hidden = outcome !== 'error';
        this.cleanReturnUrl(url);
        this.open();
        (outcome === 'success' ? success : errors)?.focus({ preventScroll: true });
      } else if (this.preview && !excludedPage(url.pathname, root.dataset.pageType)) {
        this.open();
      }
    }

    competingOverlay() {
      if (this.doc.documentElement.classList.contains('lock')) return true;
      const selector = 'dialog[open], [role="dialog"], [aria-modal="true"], x-drawer[open], x-modal[open], cart-drawer[open], header-sidebar[open], header-search[open], quick-buy-drawer[open], newsletter-popup[open], privacy-banner[open], pop-in[open]';
      return Array.from(this.doc.querySelectorAll(selector)).some(element => {
        if (element === this.root || this.root.contains(element) || element.contains(this.root) ||
          element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
        if (element.localName.includes('-') && !element.hasAttribute('open') && element.getAttribute('aria-hidden') !== 'false') return false;
        const style = this.win.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
    }

    context() {
      let focused = this.doc.activeElement;
      while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
      return {
        visible: this.doc.visibilityState === 'visible' && this.doc.hasFocus(),
        engaged: this.engaged,
        typing: !!focused?.closest('input:not([type="hidden"]), textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]'),
        competing: this.competingOverlay(),
        knownCustomer: this.root.dataset.knownCustomer === 'true',
        path: this.win.location.pathname,
        pageType: this.root.dataset.pageType
      };
    }

    update() {
      const now = this.win.Date.now();
      const elapsed = Math.max(0, Math.min(1000, now - this.lastTick));
      this.lastTick = now;
      if (!this.root.hidden) {
        if (this.competingOverlay()) this.close(false);
        return;
      }
      if (this.presented || this.preview || this.automaticDisabled) return;
      const context = this.context();
      this.policy.advance(elapsed, context);
      if (this.policy.claim(context)) this.open();
    }

    open() {
      this.previousFocus = this.doc.activeElement;
      for (const image of this.root.querySelectorAll('img[data-src]')) {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }
      this.presented = true;
      this.root.hidden = false;
      this.root.setAttribute('data-open', '');
    }

    close(persist = true) {
      if (this.root.hidden) return;
      const focusedInside = this.root.contains(this.doc.activeElement);
      this.root.hidden = true;
      this.root.removeAttribute('data-open');
      if (persist) this.policy.dismiss();
      if (focusedInside) {
        this.doc.activeElement?.blur();
        if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      }
    }

    submit(event) {
      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
        return;
      }
      const token = this.win.crypto.randomUUID();
      if (!this.policy.beginSubmission({ formId: this.form.id, token, path: this.win.location.pathname })) return;
      const url = new URL(this.win.location.href);
      url.searchParams.set('customer_posted', 'true');
      url.searchParams.set('hoya_welcome_form', this.form.id);
      url.searchParams.set('hoya_welcome_token', token);
      url.hash = this.form.id;
      this.form.querySelector('[name="return_to"]').value = url.pathname + url.search + url.hash;
    }

    trackNewsletter(form) {
      if (this.preview || challengePage(this.win.location.pathname, this.root.dataset.pageType) ||
        !(form instanceof this.win.HTMLFormElement) || !form.id || form.id === this.form.id ||
        form.getAttribute('method')?.toLowerCase() !== 'post' ||
        form.elements.namedItem('form_type')?.value !== 'customer' ||
        !String(form.elements.namedItem('contact[tags]')?.value || '').split(',').some(tag => tag.trim().toLowerCase() === 'newsletter')) return;
      const email = form.querySelector('input[type="email"][name="contact[email]"]');
      if (!email?.value.trim() || !form.checkValidity()) return;
      let returnInput = form.elements.namedItem('return_to');
      if (returnInput && (!(returnInput instanceof this.win.HTMLInputElement) || returnInput.type !== 'hidden')) return;
      let action;
      let url;
      try {
        action = new URL(form.getAttribute('action') || '', this.win.location.href);
        url = new URL(returnInput?.value || this.win.location.href, this.win.location.origin);
      } catch (_) {
        return;
      }
      if (action.origin !== this.win.location.origin || !/(^|\/)contact\/?$/.test(action.pathname) ||
        url.origin !== this.win.location.origin) return;
      const token = this.win.crypto.randomUUID();
      if (!this.policy.beginSubmission({ formId: form.id, token, path: url.pathname, source: 'newsletter' })) return;
      this.automaticDisabled = true;
      url.searchParams.set('customer_posted', 'true');
      url.searchParams.set('hoya_newsletter_form', form.id);
      url.searchParams.set('hoya_newsletter_token', token);
      url.searchParams.delete('hoya_welcome_form');
      url.searchParams.delete('hoya_welcome_token');
      url.hash = form.id;
      if (!returnInput) {
        returnInput = this.doc.createElement('input');
        returnInput.type = 'hidden';
        returnInput.name = 'return_to';
        form.append(returnInput);
      }
      returnInput.value = url.pathname + url.search + url.hash;
    }

    cleanReturnUrl(url) {
      url.searchParams.delete('hoya_welcome_form');
      url.searchParams.delete('hoya_welcome_token');
      url.searchParams.delete('customer_posted');
      if (url.hash === '#' + this.form.id) url.hash = '';
      this.win.history.replaceState(this.win.history.state, '', url.pathname + url.search + url.hash);
    }

    disconnect() {
      this.abort.abort();
      this.win.clearInterval(this.interval);
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { WelcomePolicy, WelcomePopupController, previewAllowed, excludedPage };
  }

  if (typeof window !== 'undefined' && !window.customElements.get('hoya-welcome-popup')) {
    window.customElements.define('hoya-welcome-popup', class extends window.HTMLElement {
      connectedCallback() {
        this.controller ||= new WelcomePopupController(this, window);
      }
      disconnectedCallback() {
        this.controller?.disconnect();
        this.controller = null;
      }
    });
  }
}());
